// /frontend/src/pages/dashboard/post/previews/FacebookPreview.js
import React from 'react'

const VIDEO_EXT    = ['.mp4', '.mov', '.webm', '.avi', '.m4v']
const PLACEHOLDER  = 'https://placehold.co/40x40/4267B2/FFFFFF?text=F'

const isVideoUrl = (url) =>
  url && VIDEO_EXT.some((ext) => url.toLowerCase().endsWith(ext))

function MediaItem({ url }) {
  if (!url) return null
  const video = isVideoUrl(url)
  return video ? (
    <video src={url} controls style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
  ) : (
    <img src={url} alt="media" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 4 }} />
  )
}

function MediaGrid({ files }) {
  const count = files.length
  if (count === 1) {
    return (
      <div style={{ width: 500, height: 280, borderRadius: 4, overflow: 'hidden', margin: '0 auto' }}>
        <MediaItem url={files[0].url} />
      </div>
    )
  }

  if (count === 2) {
    return (
      <div style={{ display: 'flex', width: 500, height: 280, gap: 2, margin: '0 auto' }}>
        <div style={{ flex: 1, borderRadius: 4, overflow: 'hidden' }}><MediaItem url={files[0].url} /></div>
        <div style={{ flex: 1, borderRadius: 4, overflow: 'hidden' }}><MediaItem url={files[1].url} /></div>
      </div>
    )
  }

  if (count === 3) {
    return (
      <div style={{ display: 'flex', width: 500, height: 280, gap: 2, margin: '0 auto' }}>
        <div style={{ flex: 2, borderRadius: 4, overflow: 'hidden', height: '100%' }}>
          <MediaItem url={files[0].url} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ flex: 1, borderRadius: 4, overflow: 'hidden' }}><MediaItem url={files[1].url} /></div>
          <div style={{ flex: 1, borderRadius: 4, overflow: 'hidden' }}><MediaItem url={files[2].url} /></div>
        </div>
      </div>
    )
  }

  // 4+ files: 2x2 grid. Overlay +N for extras
  return (
    <div style={{ display: 'grid', width: 500, height: 280, gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: 2, margin: '0 auto' }}>
      {files.slice(0, 4).map((f, i) => (
        <div key={i} style={{ borderRadius: 4, overflow: 'hidden', position: 'relative', width: '100%', height: '100%' }}>
          <MediaItem url={f.url} />
          {i === 3 && files.length > 4 && (
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              fontWeight: 700,
              borderRadius: 4
            }}>
              +{files.length - 4}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

export default function FacebookPostPreview({
  account,
  files,
  caption,
  postType,
  scheduleDateTime,
}) {
  const format       = account?.options?.format || 'feed'
  const avatar       = account?.userAvatar || account?.profileImage || account?.avatarUrl || PLACEHOLDER
  const displayName  = account?.displayName || 'Page Name'
  const storyLike    = format === 'story' || format === 'reel'

  // Story layout
  if (storyLike) {
    const main = files.length ? files[0].url : null
    const isVideo = isVideoUrl(main)
    return (
      <div className="border rounded bg-white mb-3" style={{ width: 200, fontFamily: 'Helvetica, Arial, sans-serif' }}>
        <div className="d-flex align-items-center p-2">
          <img
            src={avatar}
            alt="avatar"
            className="rounded-circle"
            style={{ width: 28, height: 28, objectFit: 'cover' }}
          />
          <div className="ms-2">
            <strong style={{ fontSize: 13 }}>{displayName}</strong>
            <br />
            <small className="text-muted" style={{ fontSize: 11 }}>
              {postType === 'schedule' && scheduleDateTime
                ? `Scheduled • ${new Date(scheduleDateTime).toLocaleString()}`
                : 'Just now'}
            </small>
          </div>
        </div>
        <div style={{
          width: 180,
          height: 320,
          backgroundColor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          borderRadius: 8,
          overflow: 'hidden'
        }}>
          {main ? (
            isVideo ? (
              <video src={main} controls muted style={{ maxWidth: '100%', maxHeight: '100%' }} />
            ) : (
              <img src={main} alt="story" style={{ maxWidth: '100%', maxHeight: '100%' }} />
            )
          ) : (
            <div className="bg-light" style={{ width: 180, height: 320, color: '#999', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>No media selected</div>
          )}
        </div>
      </div>
    )
  }

  // Feed: grid/album layout for multiple images or videos
  return (
    <div
      className="border rounded bg-white mb-3"
      style={{ width: 540, fontFamily: 'Helvetica, Arial, sans-serif' }}
    >
      {/* header */}
      <div className="d-flex align-items-center p-2">
        <img
          src={avatar}
          alt="avatar"
          className="rounded-circle"
          style={{ width: 40, height: 40, objectFit: 'cover' }}
        />
        <div className="ms-2">
          <strong>{displayName}</strong>
          <br />
          <small className="text-muted">
            {postType === 'schedule' && scheduleDateTime
              ? `Scheduled • ${new Date(scheduleDateTime).toLocaleString()}`
              : 'Just now'}
          </small>
        </div>
      </div>

      {/* caption (feed only) */}
      {caption && format === 'feed' && (
        <div className="px-3 pb-2">
          <span style={{ whiteSpace: 'pre-wrap' }}>{caption}</span>
        </div>
      )}

      {/* media (multi-media grid/album/slider) */}
      <MediaGrid files={files} />

      {/* footer actions (not for stories) */}
      {format !== 'story' && (
        <div className="d-flex align-items-center justify-content-around py-2 border-top">
          <button className="btn btn-light flex-grow-1">👍 Like</button>
          <button className="btn btn-light flex-grow-1">💬 Comment</button>
          <button className="btn btn-light flex-grow-1">↗ Share</button>
        </div>
      )}
    </div>
  )
}
